import React from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon, CloudArrowUpIcon, FolderPlusIcon, DocumentPlusIcon, CogIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { ProcessingPhase, ParsedUploadProgressEvent } from '../../../../../types/upload';
import { useAdminTranslation } from '../../../../../modules/i18n';

interface ProcessingStepProps {
  phase: ProcessingPhase;
  progress: number;
  message: string;
  progressEvent?: ParsedUploadProgressEvent | null;
  onComplete?: () => void;
  onCancel: () => void;
}

const phaseConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  idle: { icon: SparklesIcon, color: 'text-gray-400', label: 'Ready to process' },
  initializing: { icon: CogIcon, color: 'text-blue-500', label: 'Initializing...' },
  uploading: { icon: CloudArrowUpIcon, color: 'text-blue-500', label: 'Uploading files...' },
  validating: { icon: ArrowPathIcon, color: 'text-purple-500', label: 'Validating...' },
  processing: { icon: DocumentPlusIcon, color: 'text-primary', label: 'Processing...' },
  complete: { icon: CheckCircleIcon, color: 'text-green-500', label: 'Complete!' },
  error: { icon: XCircleIcon, color: 'text-red-500', label: 'Error' },
  cancelled: { icon: XCircleIcon, color: 'text-yellow-500', label: 'Cancelled' }
};

export const ProcessingStep: React.FC<ProcessingStepProps> = ({ phase, progress, message, progressEvent, onComplete, onCancel }) => {
  const { t } = useAdminTranslation();
  const config = phaseConfig[phase] || phaseConfig.idle;
  const Icon = config.icon;

  const isProcessing = ['initializing', 'uploading', 'validating', 'processing'].includes(phase);
  const isComplete = phase === 'complete';
  const isError = phase === 'error';
  const isCancelled = phase === 'cancelled';

  return (
    <div className='space-y-8'>
      {/* Main status */}
      <div className='flex flex-col items-center justify-center py-8'>
        {/* Icon with animation */}
        <div
          className={`mb-6 rounded-full p-6 ${
            isComplete
              ? 'bg-green-100 dark:bg-green-900/30'
              : isError || isCancelled
                ? 'bg-red-100 dark:bg-red-900/30'
                : 'bg-primary/10 dark:bg-primary/20'
          }`}
        >
          <Icon
            className={`h-16 w-16 ${config.color} ${isProcessing ? 'animate-spin' : ''}`}
            style={isProcessing ? { animationDuration: '2s' } : undefined}
          />
        </div>

        {/* Status text */}
        <h3 className='mb-2 text-2xl font-bold text-gray-800 dark:text-dark-text'>
          {isComplete
            ? t('content.documents.wizard.processing.complete')
            : isError
              ? t('content.documents.wizard.processing.error')
              : isCancelled
                ? t('content.documents.wizard.processing.cancelled')
                : isProcessing
                  ? t('content.documents.wizard.processing.inProgress')
                  : t('content.documents.wizard.processing.ready')}
        </h3>
        <p className='text-gray-600 dark:text-dark-text-secondary'>{message || config.label}</p>

        {/* Progress bar */}
        {isProcessing && (
          <div className='mt-6 w-full max-w-md'>
            <div className='mb-2 flex justify-between text-sm'>
              <span className='text-gray-600 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.progress')}</span>
              <span className='font-medium text-primary'>{Math.round(progress)}%</span>
            </div>
            <div className='h-3 overflow-hidden rounded-full bg-gray-200 dark:bg-dark-background-tertiary'>
              <div
                className='h-full rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-500'
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Progress details */}
      {progressEvent && isProcessing && (
        <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-border-dark dark:bg-dark-background-secondary/50'>
          <h4 className='mb-3 font-medium text-gray-800 dark:text-dark-text'>{t('content.documents.wizard.processing.details')}</h4>
          <div className='grid grid-cols-2 gap-4 text-sm md:grid-cols-4'>
            <div>
              <p className='text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.filesProcessed')}</p>
              <p className='font-medium text-gray-800 dark:text-dark-text'>
                {progressEvent.files_processed} / {progressEvent.files_total}
              </p>
            </div>
            <div>
              <p className='text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.foldersCreated')}</p>
              <p className='font-medium text-gray-800 dark:text-dark-text'>{progressEvent.folders_created}</p>
            </div>
            <div>
              <p className='text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.documentsCreated')}</p>
              <p className='font-medium text-gray-800 dark:text-dark-text'>{progressEvent.documents_created}</p>
            </div>
            <div>
              <p className='text-gray-500 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.assetsUploaded')}</p>
              <p className='font-medium text-gray-800 dark:text-dark-text'>{progressEvent.assets_uploaded}</p>
            </div>
          </div>
          {progressEvent.current_file && (
            <div className='mt-3 border-t border-gray-200 pt-3 dark:border-border-dark'>
              <p className='text-xs text-gray-500 dark:text-dark-text-secondary'>
                {t('content.documents.wizard.processing.currentFile')}: {progressEvent.current_file}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Completion stats */}
      {isComplete && progressEvent && (
        <div className='rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/20'>
          <h4 className='mb-4 text-center font-medium text-green-800 dark:text-green-200'>{t('content.documents.wizard.processing.summary')}</h4>
          <div className='grid grid-cols-2 gap-4 text-center md:grid-cols-4'>
            <div>
              <DocumentPlusIcon className='mx-auto mb-2 h-8 w-8 text-green-600' />
              <p className='text-2xl font-bold text-gray-800 dark:text-dark-text'>{progressEvent.documents_created}</p>
              <p className='text-xs text-gray-600 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.documents')}</p>
            </div>
            <div>
              <FolderPlusIcon className='mx-auto mb-2 h-8 w-8 text-yellow-600' />
              <p className='text-2xl font-bold text-gray-800 dark:text-dark-text'>{progressEvent.folders_created}</p>
              <p className='text-xs text-gray-600 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.folders')}</p>
            </div>
            <div>
              <CloudArrowUpIcon className='mx-auto mb-2 h-8 w-8 text-blue-600' />
              <p className='text-2xl font-bold text-gray-800 dark:text-dark-text'>{progressEvent.assets_uploaded}</p>
              <p className='text-xs text-gray-600 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.assets')}</p>
            </div>
            <div>
              <SparklesIcon className='mx-auto mb-2 h-8 w-8 text-purple-600' />
              <p className='text-2xl font-bold text-gray-800 dark:text-dark-text'>{progressEvent.assets_deduplicated}</p>
              <p className='text-xs text-gray-600 dark:text-dark-text-secondary'>{t('content.documents.wizard.processing.deduplicated')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error details */}
      {isError && progressEvent && progressEvent.errors.length > 0 && (
        <div className='rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <h4 className='mb-3 font-medium text-red-800 dark:text-red-200'>{t('content.documents.wizard.processing.errorDetails')}</h4>
          <ul className='space-y-2'>
            {progressEvent.errors.map((error, index) => (
              <li key={index} className='rounded bg-white p-3 text-sm dark:bg-dark-background-secondary'>
                <p className='text-red-700 dark:text-red-300'>{error.message}</p>
                {error.file_path && <p className='mt-1 text-xs text-red-500'>{error.file_path}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action buttons */}
      <div className='flex justify-center space-x-4'>
        {isComplete && onComplete && (
          <button
            onClick={onComplete}
            className='inline-flex items-center space-x-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-8 py-3 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-green-500/20'
          >
            <CheckCircleIcon className='h-5 w-5' />
            <span>{t('content.documents.wizard.processing.doneButton')}</span>
          </button>
        )}

        {isProcessing && (
          <button
            onClick={onCancel}
            className='inline-flex items-center space-x-2 rounded-lg border border-red-300 bg-white px-6 py-3 text-red-600 transition-colors hover:bg-red-50 dark:border-red-600 dark:bg-dark-background-secondary dark:hover:bg-dark-background-hover'
          >
            <XCircleIcon className='h-5 w-5' />
            <span>{t('content.documents.wizard.processing.cancelButton')}</span>
          </button>
        )}
      </div>
    </div>
  );
};
